import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from 'app/shared/message';
import { first } from 'rxjs';
import { ConnectContactToCrediflowGQL } from './graphql/crediflow-connect-contact.mutation.generated';
import { SearchCrediflowGQL } from './graphql/crediflow-search.query.generated';

@Component({
  selector: 'app-crediflow',
  templateUrl: './crediflow.component.html',
})
export class CrediflowComponent implements OnChanges {
  @Input() public contactId: number;
  @Input() public orgNr = '';
  @Input() public isConnected = false;
  @Input() public isAccepted = false;
  @Output() public dialogDismiss = new EventEmitter<void>();

  public crediflowForm: FormGroup;
  public isLoading: boolean;
  public crediflowSearchResult: {
    partyId: number;
    cin: string;
    gln: string;
    name: string;
    contactName: string;
    email: string;
    phone: string;
    requiresConfirmation?: boolean;
  }[];

  constructor(
    private fb: FormBuilder,
    private searchCrediflowGQL: SearchCrediflowGQL,
    private connectContactToCrediflowGQL: ConnectContactToCrediflowGQL,
    private messageService: MessageService
  ) {
    this.crediflowForm = this.fb.group({
      orgNr: [this.orgNr],
      gln: [''],
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.orgNr) {
      const orgNr = changes.orgNr.currentValue;
      this.crediflowForm.patchValue({
        orgNr: orgNr ?? '',
      });

      if (orgNr) {
        this.onSearch();
      }
    }
  }

  public onSearch(): void {
    const orgNr = this.crediflowForm.get('orgNr')?.value;
    const gln = this.crediflowForm.get('gln')?.value;

    this.searchCrediflowGQL
      .fetch(
        {
          orgNr,
          gln,
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe(first())
      .subscribe({
        next: result => {
          const edges = result?.data?.company?.crediflowSearch?.edges ?? [];
          this.crediflowSearchResult = edges
            .map(edge => edge?.node)
            .map(node => ({
              partyId: node.id,
              cin: node.cin,
              gln: node.gln,
              name: node.name,
              contactName: node.contactname,
              email: node.email,
              phone: node.phone,
              requiresConfirmation: node.requiresConfirmation,
            }));
        },
        error: () => {
          this.crediflowSearchResult = [];
          this.messageService.insertData({
            type: 'error',
            textArray: ['Ett fel uppstod vid sökning'],
          });
        },
      });
  }

  public onRemoveConnection(): void {
    this.isLoading = true;

    this.connectContactToCrediflowGQL
      .mutate({
        contactId: Number(this.contactId),
        partyId: null,
      })
      .pipe(first())
      .subscribe({
        next: data => {
          const mutationData = data.data?.connectContactToCrediflowMutation;
          const mutationDetails = mutationData?.mutationDetails?.[0];
          const errors = mutationDetails?.errorsMsgs ?? [];
          const isMutationSuccessful = errors.length === 0;

          if (isMutationSuccessful) {
            this.dialogDismiss.emit();
            this.crediflowForm.reset();

            this.messageService.insertData({
              severity: 'success',
              summary: 'Koppling borttagen',
            });
          } else {
            this.messageService.insertDataFromMutation(mutationData);
          }

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.messageService.insertData({
            severity: 'error',
            summary: 'Ett fel uppstod vid borttagning av koppling',
          });
        },
      });
  }

  public onActivateOrRequestConfirmation(partyId: number): void {
    this.isLoading = true;

    this.connectContactToCrediflowGQL
      .mutate({
        contactId: Number(this.contactId),
        partyId,
      })
      .pipe(first())
      .subscribe({
        next: data => {
          const mutationData = data.data?.connectContactToCrediflowMutation;
          const mutationDetails = mutationData?.mutationDetails?.[0];
          const errors = mutationDetails?.errorsMsgs ?? [];
          const isMutationSuccessful = errors.length === 0;

          if (mutationData) {
            this.messageService.insertDataFromMutation(mutationData);
          }

          if (isMutationSuccessful) {
            this.dialogDismiss.emit();
            this.crediflowForm.reset();
          }

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.messageService.insertData({
            severity: 'error',
            summary: 'Ett fel uppstod vid aktivering',
          });
        },
      });
  }
}
